// Componentes do painel. O VISUAL é o do design 10/2026 (visual-2026-10/DESIGN.md, pedido do Erik); as regras de
// comportamento são as do TELAS §1: pouca coisa por tela, detalhe atrás de clique (ficha, modal ou ⋯). Texto que veio de
// cliente, IA ou auditora é SEMPRE texto puro — nada de HTML cru em lugar nenhum (inclusive na linha ao vivo e nos avisos).
// Movimento: só responde a ação ou a dado novo (DESIGN §5); com prefers-reduced-motion (ou no teste) nada se mexe.
import clsx from 'clsx';
import { Check, ChevronDown, CircleAlert, RotateCw, SlidersHorizontal, X, type LucideIcon } from 'lucide-react';
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useTempoReal, type UltimoEvento } from '../nucleo/tempo-real';

// ---------------- movimento ----------------
/** Sem movimento: o aparelho pediu (prefers-reduced-motion) ou é teste (jsdom). Aí o placar nasce no valor e nada anima. */
export const SEM_MOV: boolean =
  typeof window === 'undefined' ||
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  /jsdom/i.test(navigator.userAgent);

/**
 * PLACAR (DESIGN §5.3). Rola algarismo por algarismo quando o número é novo NESTA SESSÃO ou mudou de verdade.
 * Re-render com o mesmo valor e troca de tela NÃO rolam (memória por chave). No fim volta a ser texto simples.
 */
const PLACAR_VISTO = new Map<string, string>();
// constante: nunca dado de fora
const FITA = '<span>0</span><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span><span>7</span><span>8</span><span>9</span>';

export function placar(el: HTMLElement, texto: string, chave: string): void {
  const antes = PLACAR_VISTO.get(chave);
  // o StrictMode (dev) roda o efeito duas vezes no mesmo elemento: a 2ª vez não pode matar a rolagem que a 1ª começou
  if (antes === texto && el.dataset.rolando === texto) return;
  PLACAR_VISTO.set(chave, texto);
  el.setAttribute('aria-label', texto);
  if (SEM_MOV || antes === texto) {
    el.textContent = texto;
    return;
  }
  const velho = (antes ?? '').padStart(texto.length).slice(-texto.length); // alinha pela direita (99 -> 100)
  el.textContent = '';
  el.dataset.rolando = texto;
  const fitas: [HTMLElement, number][] = [];
  [...texto].forEach((ch, i) => {
    if (!/\d/.test(ch)) {
      const s = document.createElement('span');
      s.textContent = ch;
      s.setAttribute('aria-hidden', 'true');
      el.append(s);
      return;
    }
    const dig = document.createElement('span');
    dig.className = 'dig';
    dig.setAttribute('aria-hidden', 'true');
    const fita = document.createElement('span');
    fita.className = 'fita';
    fita.innerHTML = FITA;
    const v = velho[i] ?? '';
    fita.style.transform = `translateY(-${(/\d/.test(v) ? Number(v) : 0) * 10}%)`;
    fita.style.transitionDelay = `${i * 28}ms`;
    if (antes !== undefined && v !== ch) dig.classList.add('mudou');
    dig.append(fita);
    el.append(dig);
    fitas.push([fita, Number(ch)]);
  });
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      for (const [f, d] of fitas) f.style.transform = `translateY(-${d * 10}%)`;
    }),
  );
  // terminada a rolagem, volta a ser texto simples (DOM leve, número copiável)
  setTimeout(() => {
    if (PLACAR_VISTO.get(chave) === texto) el.textContent = texto;
    if (el.dataset.rolando === texto) delete el.dataset.rolando;
  }, 1100 + texto.length * 28);
}

/** Número em placar. Sem filhos de propósito: o React não disputa o DOM com o placar(). */
export function Placar({ valor, chave, className }: { valor: string; chave: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (ref.current) placar(ref.current, valor, chave);
  }, [valor, chave]);
  return <span ref={ref} className={clsx('placar', className)} />;
}

/** Barras que "enchem" (funil): na 1ª vez da sessão e quando o dado muda — a mesma memória do placar. */
const ENCHEU = new Map<string, string>();
export function useEnche(chave: string, assinatura: string): boolean {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const anima = useMemo(() => !SEM_MOV && ENCHEU.get(chave) !== assinatura, [chave, assinatura]);
  useEffect(() => {
    ENCHEU.set(chave, assinatura);
  }, [chave, assinatura]);
  return anima;
}

/** Ids que acabaram de chegar (não estavam no render anterior; nunca no 1º): ganham o banho de luz `.linha-nova`. */
export function useNovos(ids: string[]): Set<string> {
  const anteriores = useRef<Set<string> | null>(null);
  const [novos, setNovos] = useState<Set<string>>(() => new Set());
  const chave = ids.join('|');
  useEffect(() => {
    const atual = new Set(ids);
    const antes = anteriores.current;
    anteriores.current = atual;
    if (!antes || SEM_MOV) return;
    const chegaram = ids.filter((id) => !antes.has(id));
    if (chegaram.length > 0) setNovos(new Set(chegaram));
  }, [chave]); // eslint-disable-line react-hooks/exhaustive-deps
  // o apagar mora num efeito à parte: a lista mudar de novo (reordenou) antes de 1,5 s não pode cancelar o apagar
  useEffect(() => {
    if (novos.size === 0) return;
    const t = setTimeout(() => setNovos(new Set()), 1500);
    return () => clearTimeout(t);
  }, [novos]);
  return novos;
}

/** Mantém montado por `ms` depois de fechar, pra dar tempo da saída (só opacidade). Sem movimento: some na hora. */
function usePresenca(aberto: boolean, ms = 160): { montado: boolean; saindo: boolean } {
  const [montado, setMontado] = useState(aberto);
  useEffect(() => {
    if (aberto) {
      setMontado(true);
      return;
    }
    const t = setTimeout(() => setMontado(false), ms);
    return () => clearTimeout(t);
  }, [aberto, ms]);
  const fica = aberto || (!SEM_MOV && montado);
  return { montado: fica, saindo: fica && !aberto };
}

// ---------------- básicos ----------------
export type VarianteBotao = 'normal' | 'primario' | 'tonal' | 'fantasma' | 'perigo' | 'perigo-leve';

/**
 * Botão (DESIGN §4.1). primario = a UMA ação da tela (cor cheia, texto escuro); normal = secundário; tonal = ação de
 * cartão; fantasma = cancelar/links; perigo = o confirmar de coisa que não volta (cheio); perigo-leve = vermelho sem fundo.
 * `carregando` fica DENTRO do botão: com `ico`, o próprio ícone gira; sem, um aro pequeno.
 */
export function Botao({
  variante = 'normal',
  tamanho = 'md',
  carregando,
  ico: Ico,
  className,
  children,
  ...resto
}: ComponentProps<'button'> & { variante?: VarianteBotao; tamanho?: 'sm' | 'md'; carregando?: boolean; ico?: LucideIcon }) {
  return (
    <button
      type="button"
      {...resto}
      disabled={resto.disabled || carregando}
      className={clsx(
        'inline-flex shrink-0 select-none items-center justify-center gap-[7px] whitespace-nowrap rounded-[10px] border font-medium',
        'transition-[transform,background-color,border-color,color,filter] duration-[160ms] ease-curva enabled:active:scale-[.97] enabled:active:duration-[90ms]',
        'disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0',
        tamanho === 'sm' ? 'h-8 px-3 text-[13px] max-[820px]:h-11' : 'h-9 px-3.5 text-[13.5px] max-[820px]:h-11',
        variante === 'primario' && 'border-transparent bg-primary font-semibold text-primary-tinta enabled:hover:brightness-[1.07]',
        variante === 'normal' && 'border-borda-2 bg-surface-2 text-texto enabled:hover:border-borda-3',
        variante === 'tonal' && 'border-primary/40 bg-primary-soft text-primary-claro enabled:hover:border-primary/60 enabled:hover:bg-primary/20',
        variante === 'fantasma' && 'border-transparent bg-transparent text-muted enabled:hover:bg-surface-2 enabled:hover:text-texto',
        variante === 'perigo' && 'border-transparent bg-danger font-semibold text-[#2a0b0b] enabled:hover:brightness-[1.07]',
        variante === 'perigo-leve' && 'border-transparent bg-transparent text-danger enabled:hover:bg-danger/10',
        className,
      )}
    >
      {Ico ? (
        <span className={clsx('inline-flex', carregando && 'girando')}>
          <Ico />
        </span>
      ) : (
        carregando && <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
      )}
      {children}
    </button>
  );
}

/** Botão só de ícone (↻, ⋯, ✕): 36 no PC, 44 no celular. O rótulo é obrigatório (vira aria-label e dica). */
export function BotaoIcone({ rotulo, className, children, ...resto }: ComponentProps<'button'> & { rotulo: string }) {
  return (
    <button type="button" aria-label={rotulo} title={rotulo} {...resto} className={clsx('bt-ico transition-colors duration-[160ms] [&_svg]:size-[18px]', className)}>
      {children}
    </button>
  );
}

const CAMPO =
  'w-full rounded-[10px] border bg-bg px-3 text-[14px] text-texto placeholder:text-faint transition-[border-color,box-shadow] duration-[160ms] ease-curva focus:outline-none focus-visible:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(61,139,255,.25)] disabled:opacity-60';
const ROTULO = 'mb-1.5 block text-[13px] font-medium text-muted';

export function Campo({ rotulo, dica, erro, icone: Icone, className, ...resto }: InputHTMLAttributes<HTMLInputElement> & { rotulo?: string; dica?: string; erro?: string; icone?: LucideIcon }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block">
      {rotulo && <span className={ROTULO}>{rotulo}</span>}
      <span className="relative block">
        {Icone && <Icone className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />}
        <input
          {...resto}
          id={id}
          className={clsx(CAMPO, 'h-9 max-[820px]:h-11', Icone && 'pl-9', erro ? 'border-danger' : 'border-borda-2', className)}
        />
      </span>
      {erro ? <span className="mt-1 block text-[12px] text-danger">{erro}</span> : dica ? <span className="mt-1 block text-[12px] text-faint">{dica}</span> : null}
    </label>
  );
}

export function AreaTexto({ rotulo, dica, className, ...resto }: TextareaHTMLAttributes<HTMLTextAreaElement> & { rotulo?: string; dica?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block">
      {rotulo && <span className={ROTULO}>{rotulo}</span>}
      <textarea {...resto} id={id} className={clsx(CAMPO, 'min-h-24 border-borda-2 py-2 leading-[1.45]', className)} />
      {dica && <span className="mt-1 block text-[12px] text-faint">{dica}</span>}
    </label>
  );
}

export function Selecao({ rotulo, className, children, ...resto }: SelectHTMLAttributes<HTMLSelectElement> & { rotulo?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block">
      {rotulo && <span className={ROTULO}>{rotulo}</span>}
      <select {...resto} id={id} className={clsx(CAMPO, 'campo-sel h-9 border-borda-2 pr-8 max-[820px]:h-11', className)}>
        {children}
      </select>
    </label>
  );
}

/** O select nativo com cara de chip (DESIGN §4.9): pixel, pix, período, ordem, "auto 7047 ▾". `vazio` = âmbar. */
export function SelecaoChip({ vazio, className, ...resto }: ComponentProps<'select'> & { vazio?: boolean }) {
  return <select {...resto} className={clsx('chip-sel', vazio && 'vazio', className)} />;
}

export function Alternador({ ligado, aoMudar, rotulo }: { ligado: boolean; aoMudar: (v: boolean) => void; rotulo?: string }) {
  return (
    <button type="button" role="switch" aria-checked={ligado} aria-label={rotulo} onClick={() => aoMudar(!ligado)} className="inline-flex items-center gap-2.5 max-[820px]:min-h-11">
      <span className={clsx('relative h-5 w-9 shrink-0 rounded-full transition-colors duration-[160ms] ease-curva', ligado ? 'bg-primary' : 'bg-borda-2')}>
        <span className={clsx('absolute top-0.5 size-4 rounded-full bg-white shadow transition-[left] duration-[160ms] ease-curva', ligado ? 'left-[18px]' : 'left-0.5')} />
      </span>
      {rotulo && <span className="text-[13px] text-muted">{rotulo}</span>}
    </button>
  );
}

const TOM_ETIQUETA = { ok: 'vivo', warn: 'aviso', danger: 'perigo', primary: 'cor', neutro: '' } as const;

/** Etiqueta de estado (DESIGN §4.9): 22 px, raio 6. Longa demais corta com reticências e mostra inteira na dica. */
export function Etiqueta({ tom = 'neutro', titulo, className, children }: { tom?: 'neutro' | 'ok' | 'warn' | 'danger' | 'primary'; titulo?: string; className?: string; children: ReactNode }) {
  return (
    <span className={clsx('etq max-w-full', TOM_ETIQUETA[tom], className)} title={titulo ?? (typeof children === 'string' ? children : undefined)}>
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/** Ponto de 8 px (DESIGN §4.16): vivo brilha, caído fica com anel e SEM halo, aviso âmbar, neutro apagado. */
export function PontoStatus({ tom, titulo }: { tom: 'ok' | 'warn' | 'danger' | 'neutro'; titulo?: string }) {
  return (
    <span
      title={titulo}
      aria-label={titulo}
      className={clsx(
        'inline-block size-2 shrink-0 rounded-full',
        tom === 'ok' && 'bg-ok shadow-[0_0_8px_rgba(52,211,153,.8)]',
        tom === 'warn' && 'bg-warn',
        tom === 'danger' && 'bg-danger shadow-[0_0_0_3px_rgba(248,113,113,.18)]',
        tom === 'neutro' && 'bg-faint',
      )}
    />
  );
}

/** Chip de escolha (filtro ligado/desligado). O contador vai em placar. */
export function Chip({ ligado, n, className, children, ...resto }: ComponentProps<'button'> & { ligado?: boolean; n?: number }) {
  return (
    <button type="button" aria-pressed={ligado} {...resto} className={clsx('chip transition-colors duration-[160ms] hover:text-texto [&_svg]:size-4', ligado && 'on', className)}>
      {children}
      {n !== undefined && <b>{n}</b>}
    </button>
  );
}

/** Filtro fora do padrão, à vista ao lado do "filtros": o chip inteiro é o ✕ (alvo de toque grande no celular). */
export function ChipFiltro({ rotulo, aoTirar }: { rotulo: string; aoTirar: () => void }) {
  return (
    <button type="button" className="chip on" aria-label={`tirar filtro: ${rotulo}`} title="tirar este filtro" onClick={aoTirar}>
      {rotulo}
      <X className="size-3.5" aria-hidden />
    </button>
  );
}

/** "filtros N" (DESIGN §7.0): mais de 3 controles viram este botão, que abre os MESMOS controles de hoje. */
export function BotaoFiltros({ n, aberto, aoAlternar }: { n: number; aberto: boolean; aoAlternar: () => void }) {
  return (
    <button type="button" aria-expanded={aberto} onClick={aoAlternar} className={clsx('chip transition-colors duration-[160ms] hover:text-texto', (aberto || n > 0) && 'on')}>
      <SlidersHorizontal className="size-4" aria-hidden />
      filtros
      {n > 0 && <b>{n}</b>}
      <ChevronDown className={clsx('size-3.5 transition-transform duration-[160ms] ease-curva', aberto && 'rotate-180')} aria-hidden />
    </button>
  );
}

/**
 * Cartão (DESIGN §4.3): placa, raio 14, sem gradiente e sem subir no hover. Com `aoClicar` o cartão inteiro abre a ficha
 * ou o editor (Enter/Espaço também); os botões de dentro param a propagação. `tom`: vivo (halo verde), caido (borda
 * vermelha sem halo), falta (borda âmbar).
 */
export function Cartao({
  titulo,
  sub,
  acao,
  className,
  corpo,
  tom,
  aoClicar,
  children,
  ...resto
}: {
  titulo?: ReactNode;
  sub?: ReactNode;
  acao?: ReactNode;
  className?: string;
  corpo?: string;
  tom?: 'vivo' | 'caido' | 'falta';
  aoClicar?: () => void;
  children: ReactNode;
} & Omit<ComponentProps<'section'>, 'title' | 'onClick' | 'children'>) {
  const cabeca = Boolean(titulo || acao);
  return (
    <section
      {...resto}
      tabIndex={aoClicar ? 0 : undefined}
      onClick={aoClicar}
      onKeyDown={
        aoClicar
          ? (e) => {
              if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault();
                aoClicar();
              }
            }
          : undefined
      }
      className={clsx(
        'min-w-0 rounded-[14px] border bg-surface shadow-n1',
        tom === 'vivo' ? 'border-ok/30 shadow-[inset_0_1px_0_rgba(255,255,255,.035),0_0_24px_-8px_rgba(52,211,153,.45)]' : tom === 'caido' ? 'border-danger/35' : tom === 'falta' ? 'border-warn/35' : 'border-borda',
        aoClicar && 'cartao-clica',
        className,
      )}
    >
      {cabeca && (
        <header className="flex items-start justify-between gap-3 px-4 pt-4 max-[820px]:px-3.5 max-[820px]:pt-3.5">
          <div className="min-w-0">
            <h2 className="font-titulo text-[16px] font-semibold leading-tight text-branco">{titulo}</h2>
            {sub && <p className="mt-1 text-[13px] text-muted">{sub}</p>}
          </div>
          {acao}
        </header>
      )}
      <div className={clsx(corpo ?? 'p-4 max-[820px]:p-3.5', cabeca && corpo === undefined && 'pt-3 max-[820px]:pt-3')}>{children}</div>
    </section>
  );
}

/** Vazio (DESIGN §4.15): caixa tracejada, título + uma frase + até 1 botão. `leve` = sem a caixa (dentro de painel). */
export function Vazio({ titulo, descricao, acao, leve, erro }: { titulo: string; descricao?: string; acao?: ReactNode; leve?: boolean; erro?: boolean }) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center gap-1.5 text-center',
        leve ? 'px-4 py-8' : 'rounded-[14px] border border-dashed px-6 py-10',
        !leve && (erro ? 'border-danger/40' : 'border-borda-2'),
      )}
    >
      <p className="text-[14.5px] font-semibold text-texto">{titulo}</p>
      {descricao && <p className="max-w-md text-[13px] text-muted">{descricao}</p>}
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  );
}

/** Carregando SÓ no lugar (nunca faixa ou spinner global). */
export function Esqueleto({ linhas = 3, className }: { linhas?: number; className?: string }) {
  return (
    <div className={clsx('space-y-2.5', className)} aria-hidden>
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="esq h-4" style={{ width: `${70 + ((i * 13) % 30)}%` }} />
      ))}
    </div>
  );
}

export function Dica({ texto, children }: { texto: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden -translate-x-1/2 whitespace-pre rounded-[10px] border border-borda-2 bg-pop px-2.5 py-1.5 text-[12px] text-muted shadow-n2 group-hover:block">
        {texto}
      </span>
    </span>
  );
}

// ---------------- números grandes ----------------
/** "1.341,26" (sem o R$): o R$ vai à parte, pequeno e apagado (DESIGN §4.4). */
export function numeroDinheiro(centavos: number): string {
  const v = (Math.abs(centavos) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return centavos < 0 ? `-${v}` : v;
}

/** KPI (DESIGN §4.4): rótulo 13 em caixa normal, número 36 em placar (bom = verde, ruim = vermelho), sub 12. */
export function Kpi({
  rotulo,
  valor,
  chave,
  sub,
  tom,
  dinheiro,
  carregando,
  dica,
  className,
}: {
  rotulo: string;
  valor: string;
  chave: string;
  sub?: ReactNode;
  tom?: 'bom' | 'ruim';
  dinheiro?: boolean;
  carregando?: boolean;
  dica?: string;
  className?: string;
}) {
  return (
    <div title={dica} className={clsx('min-w-0 rounded-[14px] border border-borda bg-surface px-[18px] py-4 shadow-n1 max-[820px]:px-3.5 max-[820px]:py-3', className)}>
      <div className="truncate text-[13px] font-medium text-muted">{rotulo}</div>
      <div className={clsx('mt-2.5 flex items-baseline whitespace-nowrap text-[36px] leading-none max-[820px]:text-[30px]', tom === 'bom' ? 'text-ok' : tom === 'ruim' ? 'text-danger' : 'text-branco')}>
        {carregando ? (
          <span className="esq block h-9 w-[62%]" />
        ) : (
          <>
            {dinheiro && <span className="cifrao">R$</span>}
            <Placar valor={valor} chave={chave} />
          </>
        )}
      </div>
      {sub && <div className="mt-2 truncate text-[12px] text-faint">{sub}</div>}
    </div>
  );
}

/**
 * Os KPIs pequenos numa placa só (enxerto da ousada): rótulo à esquerda, número de 20 à direita; 2×2 no celular.
 * `titulo` vai na dica; `aviso` (o que pede atenção, ex.: "2 esperando você") fica à vista, em âmbar, embaixo do rótulo.
 */
export function KpiFaixa({ itens }: { itens: { rotulo: string; valor: string; chave: string; dinheiro?: boolean; titulo?: string; aviso?: string }[] }) {
  return (
    <div className="kpi-faixa">
      {itens.map((i) => (
        <div key={i.chave} title={i.titulo}>
          {/* max-w-full: no celular a célula vira coluna (itens à esquerda) e o pedaço ficava com a largura do PRÓPRIO
              texto — o "cortar" nunca entrava e o aviso invadia a célula do lado (20/10, "o Meta ainda não fechou o
              período" por cima do 2.217). No celular o aviso quebra linha em vez de cortar. */}
          <span className="min-w-0 max-w-full">
            <span className="r block truncate">{i.rotulo}</span>
            {i.aviso && <span className="mt-0.5 block truncate text-[12px] leading-snug text-warn max-[820px]:whitespace-normal">{i.aviso}</span>}
          </span>
          <span className="ml-auto flex items-baseline text-[20px] leading-none text-branco max-[820px]:ml-0">
            {i.dinheiro && <span className="cifrao">R$</span>}
            <Placar valor={i.valor} chave={i.chave} />
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------- linha ao vivo ----------------
/**
 * Linha ao vivo (DESIGN §4.6): pílula com o ponto, uma linha de texto e o ↻ = atualizar esta tela. O texto vem SÓ do que a
 * tela já tem (nenhuma consulta nova): prioridade problema (âmbar) > esperando você (vira link) > "chegou agora" (evento
 * do websocket com um dos `tipos` da tela, por 60 s) > frescor. Tela com `tipos` é "ao vivo" (ponto verde respirando).
 */
export function AoVivo({
  frescor,
  tipos,
  textoEvento = 'chegou agora',
  aviso,
  esperando,
  aoAtualizar,
  atualizando,
  titulo,
  estado,
}: {
  frescor: string;
  tipos?: string[];
  textoEvento?: string;
  aviso?: string | null;
  esperando?: { texto: string; para: string } | null;
  aoAtualizar?: () => void;
  atualizando?: boolean;
  titulo?: string;
  estado?: 'vivo' | 'neutro' | 'caiu';
}) {
  const { vivo, ultimoEvento } = useTempoReal();
  const temTempoReal = Boolean(tipos?.length);
  // guarda o último evento DESTA tela: um de outro tipo (mensagem nova chega toda hora) não pode apagar o "chegou agora"
  const tiposChave = tipos?.join('|') ?? '';
  const [relevante, setRelevante] = useState<UltimoEvento | null>(() =>
    ultimoEvento && tiposChave.split('|').includes(ultimoEvento.tipo) ? ultimoEvento : null,
  );
  useEffect(() => {
    if (ultimoEvento && tiposChave.split('|').includes(ultimoEvento.tipo)) setRelevante(ultimoEvento);
  }, [ultimoEvento, tiposChave]);
  const [, setTique] = useState(0);
  const [bate, setBate] = useState(false);
  const recente = relevante !== null && Date.now() - relevante.em < 60_000;

  // evento novo da tela: anel uma vez; depois de 60 s volta pro frescor (um timer só, nada em laço)
  useEffect(() => {
    if (!relevante) return;
    setBate(true);
    const t1 = setTimeout(() => setBate(false), 900);
    const t2 = setTimeout(() => setTique((n) => n + 1), Math.max(0, 60_000 - (Date.now() - relevante.em)) + 50);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [relevante]);

  let classe: '' | 'vivo' | 'caiu' = estado === 'vivo' ? 'vivo' : estado === 'caiu' ? 'caiu' : temTempoReal ? 'vivo' : '';
  let texto = frescor;
  let para: string | null = null;
  if (aviso) {
    classe = 'caiu';
    texto = aviso;
  } else if (temTempoReal && !vivo) {
    classe = 'caiu';
    texto = 'reconectando…';
  } else if (esperando) {
    texto = esperando.texto;
    para = esperando.para;
  } else if (recente) {
    classe = 'vivo';
    texto = textoEvento;
  }
  // "atualizando…" e o ↻ girando só quando ELE apertou o ↻: a recarga que o websocket pede é silenciosa (senão a pílula
  // piscaria a cada mensagem que chega)
  const [pedi, setPedi] = useState(false);
  useEffect(() => {
    if (!pedi) return;
    if (!atualizando) {
      const t = setTimeout(() => setPedi(false), 400);
      return () => clearTimeout(t);
    }
  }, [pedi, atualizando]);
  const girando = pedi && Boolean(atualizando);
  if (girando) texto = 'atualizando…';
  const dica = [titulo, texto !== frescor ? frescor : null].filter(Boolean).join(' · ') || undefined;

  // troca de texto: o velho sobe e sai, o novo sobe de baixo (340 ms)
  const [linhas, setLinhas] = useState<{ k: number; texto: string; entra?: boolean; sai?: boolean }[]>(() => [{ k: 0, texto }]);
  useEffect(() => {
    setLinhas((atual) => {
      const ult = atual[atual.length - 1]!;
      if (ult.texto === texto) return atual;
      return SEM_MOV ? [{ k: ult.k + 1, texto }] : [{ ...ult, entra: false, sai: true }, { k: ult.k + 1, texto, entra: true }];
    });
  }, [texto]);
  useEffect(() => {
    if (linhas.length < 2) return;
    const t = setTimeout(() => setLinhas((a) => a.slice(-1).map((x) => ({ ...x, entra: false }))), 340);
    return () => clearTimeout(t);
  }, [linhas]);

  const janela = linhas.map((l) => (
    <span key={l.k} className={clsx('item', l.entra && 'entra', l.sai && 'sai')} aria-hidden={l.sai || undefined}>
      {l.texto}
    </span>
  ));
  return (
    <div className={clsx('aovivo', classe, bate && 'bate', !aoAtualizar && 'pr-3')} title={dica} aria-live="polite">
      <span className="ponto-vivo" aria-hidden />
      {para ? (
        // o link é a área de toque (a pílula toda, 44 no celular); a janela de dentro é que recorta a troca de texto
        <Link to={para} className="-my-px flex min-w-0 flex-1 items-center self-stretch hover:underline">
          <span className="janela">{janela}</span>
        </Link>
      ) : (
        <span className="janela">{janela}</span>
      )}
      {aoAtualizar && (
        <button
          type="button"
          className={clsx('bt-ico transition-colors duration-[160ms]', girando && 'girando')}
          aria-label="atualizar"
          title="atualizar esta tela"
          onClick={() => {
            setPedi(true);
            aoAtualizar();
          }}
          disabled={girando}
        >
          <RotateCw className="size-[15px]" />
        </button>
      )}
    </div>
  );
}

/** "atualizado às 14:05": o frescor de uma consulta (dataUpdatedAt do TanStack Query), sem relógio rodando. */
export function frescorDe(atualizadoEm: number, prefixo = 'atualizado'): string {
  if (!atualizadoEm) return 'buscando…';
  const hora = new Date(atualizadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
  return `${prefixo} às ${hora}`;
}

// ---------------- estrutura ----------------
/**
 * Abas. O padrão é o SEGMENTADO (DESIGN §4.8): o indicador desliza até a escolhida (240 ms), o contador vai em placar
 * e a faixa rola de lado dentro dela no celular (a página nunca arrasta). `sublinhado` fica pra quem precisar.
 */
export function Abas<T extends string>({
  abas,
  atual,
  aoTrocar,
  variante = 'segmentado',
  className,
  rotulo,
}: {
  abas: { id: T; rotulo: string; contador?: number }[];
  atual: T;
  aoTrocar: (id: T) => void;
  variante?: 'segmentado' | 'sublinhado';
  className?: string;
  rotulo?: string;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const ind = useRef<HTMLSpanElement>(null);
  const primeira = useRef(true);
  const posicionar = useCallback(() => {
    const i = ind.current;
    const on = caixa.current?.querySelector<HTMLElement>('[aria-selected=true]');
    if (!i) return;
    if (!on) {
      i.style.width = '0px';
      return;
    }
    i.style.width = `${on.offsetWidth}px`;
    i.style.transform = `translateX(${on.offsetLeft}px)`;
  }, []);
  const assinatura = abas.map((a) => `${a.id}:${a.rotulo}:${a.contador ?? ''}`).join('|');
  useLayoutEffect(() => {
    const i = ind.current;
    if (!i) return;
    if (primeira.current) {
      // a 1ª posição é sem animação; depois disso desliza
      i.style.transition = 'none';
      posicionar();
      void i.offsetWidth;
      i.style.transition = '';
      primeira.current = false;
    } else posicionar();
  }, [atual, assinatura, posicionar]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !caixa.current) return;
    const ro = new ResizeObserver(() => posicionar());
    ro.observe(caixa.current);
    return () => ro.disconnect();
  }, [posicionar]);

  if (variante === 'sublinhado') {
    return (
      <div role="tablist" aria-label={rotulo} className={clsx('flex gap-1 border-b border-borda', className)}>
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={a.id === atual}
            onClick={() => aoTrocar(a.id)}
            className={clsx('-mb-px border-b-2 px-3 py-2 text-[14px] transition-colors', a.id === atual ? 'border-primary text-branco' : 'border-transparent text-muted hover:text-texto')}
          >
            {a.rotulo}
            {a.contador !== undefined && a.contador > 0 && <span className="ml-1.5 text-[12px] text-faint">{a.contador}</span>}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div ref={caixa} role="tablist" aria-label={rotulo} className={clsx('seg', className)}>
      <span ref={ind} className="seg-ind" aria-hidden />
      {abas.map((a) => (
        <button key={a.id} type="button" role="tab" aria-selected={a.id === atual} onClick={() => aoTrocar(a.id)} className="transition-colors duration-[160ms]">
          {a.rotulo}
          {a.contador !== undefined && a.contador > 0 && <span className="q">{a.contador}</span>}
        </button>
      ))}
    </div>
  );
}

/** Guarda o que estava na tela enquanto ela fecha (a saída de 160 ms não pode mostrar "apagar undefined?"). */
function useUltimo<T>(aberto: boolean, valor: T): T {
  const ref = useRef(valor);
  if (aberto) ref.current = valor;
  return aberto ? valor : ref.current;
}

/**
 * Ficha (DESIGN §4.13): flutua a 12 px das bordas, raio 20, entra de lado (240 ms). No celular vira folha de baixo com
 * puxador. Fecha pelo ✕, pelo Esc ou no véu.
 */
export function Gaveta({
  aberta,
  aoFechar,
  titulo,
  sub,
  largura = 'md',
  rodape,
  children,
}: {
  aberta: boolean;
  aoFechar: () => void;
  titulo?: ReactNode;
  sub?: ReactNode;
  largura?: 'md' | 'lg';
  rodape?: ReactNode;
  children: ReactNode;
}) {
  useEsc(aberta, aoFechar);
  const { montado, saindo } = usePresenca(aberta);
  const v = useUltimo(aberta, { titulo, sub, rodape, children });
  if (!montado) return null;
  return createPortal(
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true">
      <div className={clsx('veu', saindo && 'sai')} onClick={aoFechar} />
      <aside className={clsx('ficha', largura === 'lg' && 'larga', saindo && 'sai')}>
        <div className="puxador" />
        <header className="flex items-start gap-3 border-b border-borda py-3.5 pl-5 pr-3 max-[820px]:pl-4">
          <div className="min-w-0 flex-1 pt-1">
            <h2 className="font-titulo text-[19px] font-semibold leading-tight text-branco">{v.titulo}</h2>
            {v.sub && <div className="mt-1 text-[13px] text-muted">{v.sub}</div>}
          </div>
          <BotaoIcone rotulo="fechar" onClick={aoFechar}>
            <X />
          </BotaoIcone>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 max-[820px]:p-4">{v.children}</div>
        {v.rodape && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-borda px-5 py-3 max-[820px]:px-4">{v.rodape}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

/** Modal (DESIGN §4.12): centralizado, raio 20, sobe 14 px; no celular vira folha de baixo. No NEXUS o véu fecha. */
export function Modal({ aberto, aoFechar, titulo, sub, children, rodape }: { aberto: boolean; aoFechar: () => void; titulo?: ReactNode; sub?: ReactNode; children: ReactNode; rodape?: ReactNode }) {
  useEsc(aberto, aoFechar);
  const { montado, saindo } = usePresenca(aberto);
  const v = useUltimo(aberto, { titulo, sub, children, rodape });
  if (!montado) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className={clsx('veu', saindo && 'sai')} onClick={aoFechar} />
      {/* janela mais alta que a tela rola por dentro, com título e botões sempre à vista */}
      <div className={clsx('modal-cx', saindo && 'sai')}>
        <div className="puxador" />
        {v.titulo && (
          <header className="flex items-start gap-3 pb-1 pl-5 pr-3 pt-4 max-[820px]:pt-2">
            <div className="min-w-0 flex-1 pt-1">
              <h2 className="font-titulo text-[19px] font-semibold leading-tight text-branco">{v.titulo}</h2>
              {v.sub && <div className="mt-1 text-[13px] text-muted">{v.sub}</div>}
            </div>
            <BotaoIcone rotulo="fechar" onClick={aoFechar}>
              <X />
            </BotaoIcone>
          </header>
        )}
        <div className="min-h-0 overflow-y-auto overscroll-contain px-5 py-4">{v.children}</div>
        {v.rodape && <footer className="flex flex-wrap justify-end gap-2 border-t border-borda px-5 py-3">{v.rodape}</footer>}
      </div>
    </div>,
    document.body,
  );
}

function useEsc(ativo: boolean, aoFechar: () => void): void {
  useEffect(() => {
    if (!ativo) return;
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ativo, aoFechar]);
}

/** `soCelular`: o item só aparece no celular (o secundário do cabeçalho, que no PC fica à vista). */
export type ItemMenu = { rotulo: string; aoClicar: () => void; perigo?: boolean; ico?: LucideIcon; dica?: string; separador?: boolean; soCelular?: boolean };

/**
 * Menu ⋯ (DESIGN §4.2): abre ancorado pela direita do botão, em cima de tudo (portal: o overflow do cartão não corta) e
 * vira pra cima quando não cabe. Fecha com Esc, clique fora, rolagem ou escolhendo; ↑↓ andam; o foco volta pro ⋯. O
 * perigoso vai por último, em vermelho, depois de uma linha. `gatilho` troca o ⋯ por um botão ("+ novo fluxo ▾").
 */
export function Menu({
  itens,
  rotulo = '⋯',
  rotuloAcessivel = 'mais ações',
  gatilho,
}: {
  itens: (ItemMenu | false | null | undefined)[];
  rotulo?: string;
  rotuloAcessivel?: string;
  /** `rotulo`: o nome do botão pra leitor de tela (no celular o texto some e fica só o ícone) */
  gatilho?: { conteudo: ReactNode; rotulo?: string; variante?: VarianteBotao; tamanho?: 'sm' | 'md'; ico?: LucideIcon };
}) {
  const [aberto, setAberto] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const [pos, setPos] = useState<CSSProperties>({});
  const botao = useRef<HTMLButtonElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const lista = itens.filter(Boolean) as ItemMenu[];

  // o item "só no celular" fica escondido no PC: o teclado anda só pelos que estão à vista
  const itensVisiveis = (): HTMLElement[] => [...(caixa.current?.querySelectorAll<HTMLElement>('[role=menuitem]') ?? [])].filter((b) => b.getClientRects().length > 0 || SEM_MOV);

  // o sumiço da saída (120 ms) fica guardado: reabrir antes disso cancela, senão o menu abria e sumia sozinho
  const saida = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fechar = useCallback((foco: boolean) => {
    if (SEM_MOV) setAberto(false);
    else {
      setSaindo(true);
      clearTimeout(saida.current);
      saida.current = setTimeout(() => {
        setAberto(false);
        setSaindo(false);
      }, 120);
    }
    if (foco) botao.current?.focus();
  }, []);

  const abrir = (): void => {
    clearTimeout(saida.current);
    const r = botao.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setSaindo(false);
    setAberto(true);
  };

  // não cabe embaixo: vira pra cima; e o foco vai pro primeiro item
  useLayoutEffect(() => {
    if (!aberto || !caixa.current || !botao.current) return;
    const m = caixa.current.getBoundingClientRect();
    const r = botao.current.getBoundingClientRect();
    if (m.height > 0 && m.bottom > window.innerHeight - 8 && r.top - m.height - 4 > 8) {
      setPos((p) => ({ ...p, top: r.top - m.height - 4, transformOrigin: 'bottom right' }));
    }
    itensVisiveis()[0]?.focus({ preventScroll: true });
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: Event): void => {
      const alvo = e.target as Node;
      if (caixa.current?.contains(alvo) || botao.current?.contains(alvo)) return;
      fechar(false);
    };
    const rolou = (e: Event): void => {
      if (caixa.current?.contains(e.target as Node)) return;
      fechar(false);
    };
    const mudou = (): void => fechar(false);
    window.addEventListener('mousedown', fora);
    window.addEventListener('touchstart', fora);
    window.addEventListener('scroll', rolou, true);
    window.addEventListener('resize', mudou);
    return () => {
      window.removeEventListener('mousedown', fora);
      window.removeEventListener('touchstart', fora);
      window.removeEventListener('scroll', rolou, true);
      window.removeEventListener('resize', mudou);
    };
  }, [aberto, fechar]);

  const teclar = (e: React.KeyboardEvent): void => {
    const botoes = itensVisiveis();
    const i = botoes.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      fechar(true);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = botoes.length;
      botoes[(i + (e.key === 'ArrowDown' ? 1 : n - 1) + n) % n]?.focus();
    } else if (e.key === 'Tab') fechar(false);
  };

  const alternar = (e: React.MouseEvent): void => {
    e.stopPropagation(); // o ⋯ dentro de um cartão clicável não abre a ficha
    if (aberto) fechar(false);
    else abrir();
  };

  return (
    <>
      {gatilho ? (
        <Botao ref={botao} variante={gatilho.variante ?? 'normal'} tamanho={gatilho.tamanho} ico={gatilho.ico} aria-label={gatilho.rotulo} aria-haspopup="menu" aria-expanded={aberto} onClick={alternar}>
          {gatilho.conteudo}
        </Botao>
      ) : (
        <button
          ref={botao}
          type="button"
          className="bt-ico shrink-0 transition-colors duration-[160ms]"
          aria-label={rotuloAcessivel}
          title={rotuloAcessivel}
          aria-haspopup="menu"
          aria-expanded={aberto}
          onClick={alternar}
        >
          <span aria-hidden className="text-[18px] font-semibold leading-none tracking-[.04em]">
            {rotulo}
          </span>
        </button>
      )}
      {aberto &&
        createPortal(
          <div
            ref={caixa}
            role="menu"
            className={clsx('menu3 max-w-[min(340px,calc(100vw-16px))]', saindo && 'opacity-0 transition-opacity duration-[120ms]')}
            style={pos}
            onKeyDown={teclar}
            onClick={(e) => e.stopPropagation()}
          >
            {lista.map((i, n) => (
              <Fragment key={`${i.rotulo}-${n}`}>
                {n > 0 && (i.separador || (i.perigo && !lista[n - 1]!.perigo)) && <div className="menu3-sep" role="separator" />}
                <button
                  type="button"
                  role="menuitem"
                  className={clsx('menu3-i', i.perigo && 'perigo', i.soCelular && 'min-[821px]:!hidden')}
                  onClick={() => {
                    fechar(false);
                    i.aoClicar();
                  }}
                >
                  {i.ico && <i.ico aria-hidden />}
                  <span className="min-w-0 truncate">{i.rotulo}</span>
                  {i.dica && <small>{i.dica}</small>}
                </button>
              </Fragment>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

/** Confirmação de coisa que não volta: exige digitar "apagar" (TELAS §1). O confirmar é o perigo cheio. */
export function ConfirmarPerigoso({ aberto, aoFechar, aoConfirmar, titulo, descricao, palavra = 'apagar' }: {
  aberto: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
  titulo: string;
  descricao?: string;
  palavra?: string;
}) {
  const [texto, setTexto] = useState('');
  useEffect(() => {
    if (aberto) setTexto('');
  }, [aberto]);
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            cancelar
          </Botao>
          <Botao variante="perigo" disabled={texto.trim().toLowerCase() !== palavra} onClick={aoConfirmar}>
            confirmar
          </Botao>
        </>
      }
    >
      {descricao && <p className="mb-4 text-[14px] text-muted">{descricao}</p>}
      <Campo rotulo={`digite "${palavra}" pra confirmar`} value={texto} onChange={(e) => setTexto(e.target.value)} autoFocus />
    </Modal>
  );
}

// ---------------- avisos (toast) ----------------
type Aviso = { id: number; texto: string; tom: 'ok' | 'erro'; saindo?: boolean };
const ContextoAvisos = createContext<{ avisar: (texto: string, tom?: 'ok' | 'erro') => void }>({ avisar: () => {} });

/** Aviso (DESIGN §4.14): ok some em 2,6 s; erro fica 5 s e tem ✕. O ícone diz o tom (sem faixa colorida). */
export function ProvedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const proximo = useRef(1);
  const tirar = useCallback((id: number): void => {
    if (SEM_MOV) {
      setAvisos((a) => a.filter((x) => x.id !== id));
      return;
    }
    setAvisos((a) => a.map((x) => (x.id === id ? { ...x, saindo: true } : x)));
    setTimeout(() => setAvisos((a) => a.filter((x) => x.id !== id)), 160);
  }, []);
  const avisar = useCallback(
    (texto: string, tom: 'ok' | 'erro' = 'ok'): void => {
      const id = proximo.current++;
      setAvisos((a) => [...a, { id, texto, tom }]);
      setTimeout(() => tirar(id), tom === 'erro' ? 5_000 : 2_600);
    },
    [tirar],
  );
  const valor = useMemo(() => ({ avisar }), [avisar]);
  return (
    <ContextoAvisos.Provider value={valor}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 max-[820px]:bottom-[calc(12px+env(safe-area-inset-bottom))] max-[820px]:left-3 max-[820px]:right-3">
        {avisos.map((a) => (
          <div key={a.id} role="status" className={clsx('aviso-toast pointer-events-auto max-[820px]:w-full', a.saindo && 'sai')}>
            {a.tom === 'erro' ? <CircleAlert className="size-[18px] shrink-0 text-danger" aria-hidden /> : <Check className="size-[18px] shrink-0 text-ok" aria-hidden />}
            <span className="min-w-0 flex-1 break-words">{a.texto}</span>
            {a.tom === 'erro' && (
              <BotaoIcone rotulo="fechar aviso" className="-my-1 shrink-0" onClick={() => tirar(a.id)}>
                <X />
              </BotaoIcone>
            )}
          </div>
        ))}
      </div>
    </ContextoAvisos.Provider>
  );
}

export function useAvisos(): { avisar: (texto: string, tom?: 'ok' | 'erro') => void } {
  return useContext(ContextoAvisos);
}

// ---------------- formatação ----------------
export function Dinheiro({ centavos, className }: { centavos: number | null | undefined; className?: string }) {
  return <span className={className}>{formatarDinheiro(centavos ?? 0)}</span>;
}

export function formatarDinheiro(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function Tempo({ quando, className }: { quando: string | Date | null | undefined; className?: string }) {
  if (!quando) return <span className={className}>—</span>;
  const d = typeof quando === 'string' ? new Date(quando) : quando;
  return (
    <time dateTime={d.toISOString()} title={d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} className={className}>
      {tempoRelativo(d)}
    </time>
  );
}

/** "agora", "há 5 min", "ontem" — o Arthur lê isso o dia inteiro, então é curto. */
export function tempoRelativo(quando: Date, agora = new Date()): string {
  const s = Math.round((agora.getTime() - quando.getTime()) / 1000);
  if (s < 45) return 'agora';
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  if (s < 86_400) return `há ${Math.round(s / 3600)} h`;
  if (s < 172_800) return 'ontem';
  if (s < 604_800) return `há ${Math.round(s / 86_400)} dias`;
  return quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
}

/** Texto que veio de fora (cliente, IA, auditora): sempre puro, com quebra de linha preservada. */
export function TextoPuro({ children, className }: { children: string | null | undefined; className?: string }) {
  return <span className={clsx('whitespace-pre-wrap break-words', className)}>{children ?? ''}</span>;
}
